import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { CloneSeasonResponse, SeasonApiRecord } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntity } from "./responseMappers";

const buildCloneSeasonUrl = (seasonId: string): string =>
  API_ROUTES.CLONE_SEASON.replace("{season_id}", seasonId);

const postCloneSeason = async (seasonId: string): Promise<SeasonApiRecord> => {
  const response = await getPrivateInstance().post<CloneSeasonResponse>(
    buildCloneSeasonUrl(seasonId),
    {},
  );
  return unwrapGamePassEntity(response.data, "season");
};

/**
 * Clone an existing season into a new draft via
 * `POST /game-pass-admin/v2/admin/seasons/{season_id}/clone`.
 *
 * The clone always lands as a draft, so rather than patch a single cached
 * record (as activate / cancel do) we invalidate the season lists and let
 * them refetch — the new copy then surfaces in the Drafts column. Errors
 * are propagated so the caller can surface a failure toast.
 */
export const useCloneSeason = (): UseMutationResult<
  SeasonApiRecord,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "seasons", "clone"],
    mutationFn: postCloneSeason,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
